import './Wheel.sass';

import React, { FC, useEffect, useRef } from 'react';
import useSize from '@react-hook/size';
import { WheelLot, WheelProps } from './Wheel.interface';
import { useSpring } from '@react-spring/web';

import { classNames as cn, statReachGoal } from '$utils';

class SpinnerSegment {
  radius: number;
  spinner: Spinner;
  context: CanvasRenderingContext2D;
  startAngle: number;
  endAngle: number;
  colors: string[];
  height: number;
  borderRadius: number;
  borderWidth: number;
  title: string;
  lineHeight: number;
  fontSize: number;
  fontFamily: string;
  em: number;
  padding: number;
  borderColor: string = '#1C1C1F';
  titleLines: string[];

  constructor(spinner: Spinner) {
    this.spinner = spinner;
    this.context = this.spinner.context;
    this.radius = 0;
    this.startAngle = 0;
    this.endAngle = 0;
    this.colors = [];
    this.height = 80;
    this.borderRadius = 16;
    this.borderWidth = 4;
    this.title = 'Sexting whole night long';

    this.lineHeight = 1.5;
    this.fontSize = 0;
    this.fontFamily = '"Nunito Sans", sans-serif';
    this.em = 10;
    this.padding = 16;

    this.titleLines = [];
  }

  splitEmoji(string) {
    if (Intl && 'Segmenter' in Intl) {
      // @ts-ignore
      return [...new Intl.Segmenter().segment(string)].map((x) => x.segment);
    } else {
      return [...string];
    }
  }

  calculateTitle() {
    const ctx = this.context;
    ctx.font = `800 ${this.fontSize}px ${this.fontFamily}`;
    ctx.textBaseline = 'top';

    const words = this.title.split(/\s+/);
    this.titleLines = [];
    const line: string[] = [];

    this.fontSize = 32;
    while (this.fontSize > 3) {
      this.titleLines.length = line.length = 0;
      ctx.font = `800 ${this.fontSize}px ${this.fontFamily}`;

      const textWidth =
        Math.tan(this.endAngle - this.startAngle) * (this.radius - this.height / 2) -
        this.padding * 2;
      for (let i = 0; i < words.length; i++) {
        if (ctx.measureText(line.concat(words[i]).join(' ')).width < textWidth) {
          line.push(words[i]);
        } else {
          this.titleLines.push(line.join(' '));
          line.length = 0;
          line.push(words[i]);
        }
      }
      this.titleLines.push(line.join(' '));
      this.em = ctx.measureText('M').actualBoundingBoxDescent;

      const maxLineWidth = Math.max(
        ...this.titleLines.map((line) => ctx.measureText(line).width),
      );

      if (
        this.titleLines.length * (this.em * this.lineHeight) <
          this.height - this.padding * 2 &&
        maxLineWidth < textWidth
      ) {
        break;
      }
      this.fontSize -= 1;
    }
  }

  drawTitle() {
    this.context.save();
    this.context.translate(this.spinner.center.x, this.spinner.center.y);
    this.context.rotate(
      this.spinner.rotation +
        this.startAngle +
        Math.PI / 2 -
        (this.startAngle - this.endAngle) / 2,
    );
    this.context.fillStyle = '#1C1C1F';
    this.context.font = `800 ${this.fontSize}px ${this.fontFamily}`;
    this.context.textAlign = 'center';
    this.context.textBaseline = 'top';

    const marginTop =
      (this.height - this.titleLines.length * (this.em * this.lineHeight)) / 2 +
      this.em / 4;

    this.titleLines.forEach((line, index) => {
      const letters = this.splitEmoji(line);
      const lineTop = this.radius - (marginTop + index * (this.em * this.lineHeight));
      const wordWidth = this.context.measureText(line).width;
      const wordWidthInRadians = Math.atan(wordWidth / lineTop);
      this.context.rotate(-wordWidthInRadians / 2);

      let shift = 0;
      letters.forEach((letter, i) => {
        const prevLetter = letters[i - 1] || '';
        const kerning =
          this.context.measureText(prevLetter).width +
          this.context.measureText(letter).width;
        const kerningInRadians = Math.atan(kerning / lineTop);
        this.context.rotate(kerningInRadians / 2);
        shift += kerningInRadians / 2;
        this.context.fillText(letter, 0, -lineTop);
      });
      this.context.rotate(-shift / 2);
    });

    this.context.restore();
  }

  drawRect() {
    const ctx = this.context;
    const sa = this.startAngle + this.spinner.rotation;
    const ea = this.endAngle + this.spinner.rotation;
    const r = this.radius;
    const br = this.borderRadius;
    const rr = this.borderRadius / this.radius;
    const h = this.height;
    const x = this.spinner.center.x;
    const y = this.spinner.center.y;

    ctx.beginPath();

    // fix bug on ARM safari
    ctx.moveTo(0, -1);
    ctx.lineTo(0, 0);

    ctx.moveTo(x + (r - br) * Math.cos(sa), y + (r - br) * Math.sin(sa));
    ctx.quadraticCurveTo(
      x + r * Math.cos(sa),
      y + r * Math.sin(sa),
      x + r * Math.cos(sa + rr),
      y + r * Math.sin(sa + rr),
    );
    ctx.arc(x, y, r, sa + rr, ea - rr, false);
    ctx.quadraticCurveTo(
      x + r * Math.cos(ea),
      y + r * Math.sin(ea),
      x + (r - br) * Math.cos(ea),
      y + (r - br) * Math.sin(ea),
    );
    ctx.lineTo(x + (r - h + br) * Math.cos(ea), y + (r - h + br) * Math.sin(ea));
    ctx.quadraticCurveTo(
      x + (r - h) * Math.cos(ea),
      y + (r - h) * Math.sin(ea),
      x + (r - h) * Math.cos(ea - rr),
      y + (r - h) * Math.sin(ea - rr),
    );
    ctx.arc(x, y, r - h, ea - rr, sa + rr, true);
    ctx.quadraticCurveTo(
      x + (r - h) * Math.cos(sa),
      y + (r - h) * Math.sin(sa),
      x + (r - h + br) * Math.cos(sa),
      y + (r - h + br) * Math.sin(sa),
    );
    ctx.closePath();

    const gradient = ctx.createLinearGradient(
      x + r * Math.cos(sa),
      y + r * Math.sin(sa),
      x + (r - h) * Math.cos(ea),
      y + (r - h) * Math.sin(ea),
    );

    this.colors.map((color, index) => {
      gradient.addColorStop(index, color);
    });

    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.lineWidth = this.spinner.borderWidth;
    ctx.strokeStyle = this.borderColor;
    ctx.stroke();

    // fix bug on ARM safari
    ctx.clearRect(0, 0, ctx.canvas.width, 1);
  }

  draw() {
    const fowRad = Math.PI / 2.5;
    const sa = (this.startAngle + this.spinner.rotation + Math.PI / 2) % (Math.PI * 2);
    const ea = (this.endAngle + this.spinner.rotation + Math.PI / 2) % (Math.PI * 2);
    if (sa < Math.PI * 2 - fowRad && ea > fowRad) return;
    this.drawRect();
    this.drawTitle();
  }
}

const FIGMA_WIDTH = 390;

class Spinner {
  lots: WheelLot[];
  context: CanvasRenderingContext2D;
  center: { x: number; y: number };
  radius: number;
  borderColor: string;
  rotation: number;
  rotationSpeed: number;
  gotoRotation: number;
  borderWidth: number;
  segments: SpinnerSegment[];

  constructor(context, lots, radius, center = { x: 0, y: 0 }, borderColor) {
    this.lots = lots;
    this.context = context;
    this.center = center;
    this.radius = radius;
    this.borderColor = borderColor;
    this.rotation = 0;
    this.rotationSpeed = 0;
    this.gotoRotation = 0;
    this.borderWidth = 4;
    const padding = Math.atan(this.borderWidth / this.radius) / 2;
    const zeroAngle = Math.PI / 2 - ((2 * Math.PI) / lots.length - padding) / 2;

    this.segments = lots.map((lot, index) => {
      const segment = new SpinnerSegment(this);
      segment.radius = this.radius;
      segment.borderColor = this.borderColor;
      segment.colors = lot.colors;
      segment.title = lot.title;
      segment.startAngle =
        ((index - 1) * 2 * Math.PI) / lots.length + padding - zeroAngle;
      segment.endAngle = (index * 2 * Math.PI) / lots.length - padding - zeroAngle;

      // window.requestIdleCallback(segment.calculateTitle.bind(segment), {timeout: 1000});
      segment.calculateTitle();
      return segment;
    });
  }

  draw() {
    this.segments.forEach((segment) => {
      segment.draw();
    });
  }

  setRotate(rotate) {
    this.rotation = rotate;
    this.draw();
  }

  update(delta) {
    this.rotation += ((this.gotoRotation - this.rotation) / 2000) * delta;
    this.draw();
  }

  spin(item) {
    this.rotation = 0;
    this.gotoRotation =
      20 * Math.PI +
      (Math.PI / 20) * Math.random() +
      ((2 * Math.PI) / this.lots.length) * item;
  }
}

export const Wheel: FC<WheelProps> = ({
  lots,
  currentLotIndex,
  lastRotationTime,
  onPrize,
  gameInProgress,
  onTouch,
}) => {
  const initLastRotationTime = useRef(lastRotationTime);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const spinnerRef = useRef<Spinner>();
  const touched = useRef(false);
  const previousSectionScatter = useRef(0);
  const offsetRotation = useRef(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperWidth, wrapperHeight] = useSize(wrapperRef);
  const [spring, api] = useSpring(() => ({
    rotation: 0,
    immediate: true,
    config: {
      mass: 10,
      tension: 9,
      friction: 23,
    },
    onChange() {
      const spinner = spinnerRef.current;
      if (spinner) {
        const canvas = canvasRef.current;
        canvas && spinner.context.clearRect(0, 0, canvas.width, canvas.height);
        spinner.rotation = spring.rotation.get() / 100;
        spinner.draw();
      }
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && wrapperWidth) {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = wrapperWidth * dpr;
      canvas.height = wrapperHeight * dpr;

      const widthRatio = wrapperWidth / FIGMA_WIDTH;

      if (spinnerRef.current) {
        spinnerRef.current.center = {
          x: FIGMA_WIDTH / 2,
          y: (FIGMA_WIDTH / 2) * 2.2,
        };
        spinnerRef.current.context.scale(widthRatio * dpr, widthRatio * dpr);
        spinnerRef.current.draw();
      }
    }
  }, [wrapperWidth, wrapperHeight, spinnerRef]);

  useEffect(() => {
    if (!canvasRef.current) return;
    let startAngle = 0;
    let delta = 0;
    const canvas = canvasRef.current;

    const dpr = window.devicePixelRatio || 1;

    const getOffsets = (e: PointerEvent | TouchEvent) => {
      let offsetX = 0;
      let offsetY = 0;

      if ('touches' in e) {
        const boundingRect = canvas.getBoundingClientRect();
        offsetX = e.touches[0].clientX - boundingRect.left;
        offsetY = e.touches[0].clientY - boundingRect.top;
      } else {
        canvas.setPointerCapture(e.pointerId);
        offsetX = e.offsetX;
        offsetY = e.offsetY;
      }
      return [offsetX, offsetY];
    };

    const handlePointerDown = (e: PointerEvent | TouchEvent) => {
      const [offsetX, offsetY] = getOffsets(e);

      if ('pointerId' in e) {
        e.pointerId && canvas.setPointerCapture(e.pointerId);
      }

      const spinner = spinnerRef.current;
      if (!spinner) return;
      spinner.rotationSpeed = 0;
      const ratio = canvas.width / FIGMA_WIDTH / dpr;

      startAngle =
        Math.atan2(
          offsetY - spinner.center.y * ratio,
          offsetX - spinner.center.x * ratio,
        ) - spinner.rotation;
      canvas.addEventListener('pointermove', handlePointerMove);
      canvas.addEventListener('touchmove', handlePointerMove);
      canvas.addEventListener('pointerup', handlePointerUp);
      canvas.addEventListener('touchend', handlePointerUp);

      delta = 0;
      api.stop();
    };

    const handlePointerMove = (e: PointerEvent | TouchEvent) => {
      const [offsetX, offsetY] = getOffsets(e);

      if (!touched.current) {
        onTouch();
        touched.current = true;
      }

      if ('pointerId' in e) {
        canvas.setPointerCapture(e.pointerId);
      }

      const spinner = spinnerRef.current;
      if (!spinner) return;

      const ratio = canvas.width / FIGMA_WIDTH / dpr;

      const rotation =
        Math.atan2(
          offsetY - spinner.center.y * ratio,
          offsetX - spinner.center.x * ratio,
        ) - startAngle;

      delta = rotation - spinner.rotation;

      if (Math.abs(delta) > 0.02) {
        e.stopPropagation();
        e.preventDefault();
      } else {
        delta = 0;
      }

      api.start({
        rotation: rotation * 100,
        immediate: true,
      });
    };

    const handlePointerUp = (e) => {
      e.stopPropagation();

      api.start({
        rotation: spring.rotation.get() + delta * 100 * 50,
        config: {
          mass: 5,
          friction: 220,
          tension: 220,
        },
      });

      if (e instanceof PointerEvent) {
        canvas.releasePointerCapture(e.pointerId);
      }

      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('touchmove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('touchend', handlePointerUp);
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('touchstart', handlePointerDown);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('touchstart', handlePointerDown);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('touchend', handlePointerUp);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('touchmove', handlePointerMove);
    };
  }, [canvasRef]);

  useEffect(() => {
    let raf: number | null = null;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d')!;

      // get css custom properties from canvasRef
      const borderColor = getComputedStyle(canvas).getPropertyValue(
        '--roulette-border-color',
      );
      const spinner = new Spinner(
        ctx,
        lots,
        400,
        {
          x: canvas.width / 2,
          y: canvas.height / 2,
        },
        borderColor || '#000',
      );

      spinnerRef.current = spinner;
    }
  }, [canvasRef]);

  useEffect(() => {
    if (
      lastRotationTime &&
      currentLotIndex !== null &&
      initLastRotationTime.current !== lastRotationTime
    ) {
      if (spinnerRef.current) spinnerRef.current.rotation = 0; // TODO
      spring.rotation.set(0); // TODO

      const currentRotation = spring.rotation.get() / 100;
      const sectionSize = (Math.PI * 2) / lots.length;
      api.start({
        rotation: (currentRotation + 10 * Math.PI + sectionSize * -currentLotIndex) * 100,
        onRest: () => {
          onPrize && onPrize();
        },
      });
    }
  }, [currentLotIndex, lastRotationTime]);

  return (
    <div
      ref={wrapperRef}
      className={cn('Wheel', {
        gameInProgress,
      })}
    >
      <canvas ref={canvasRef} />
    </div>
  );
};
